import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import API_BASE_URL from '../config/api';
import { getTagStyle } from '../utils/tagUtils';
import RouteFilter from '../components/RouteFilter';
import RoutePopup from '../components/RoutePopup';
import EditRoutePopup from '../components/EditRoutePopup';
import RoutesPageSkeleton from '../components/RoutesPageSkeleton';
import Pagination from '../components/Pagination';
import LoginPopup from '../components/LoginPopup';

import { useUser } from '../context/UserContext';
import styles from './RoutesPage.module.css';

function RoutesPage({ mountains }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, firebaseUser } = useUser();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    country: '',
    mountainRange: '',
    lengthCategory: '',
    elevationCategory: '',
    difficulty: '',
    routeType: '',
  });
  const [sortOrder, setSortOrder] = useState('elevation-desc');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isLoginPopupOpen, setIsLoginPopupOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 14;

  useEffect(() => {
    fetchRoutes();
  }, []);

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/routes`);
      if (!res.ok) {
        console.warn('Fetch routes failed:', res.status, res.statusText);
        setRoutes([]);
        return; 
      }
      const data = await res.json();
      if (data.success) {
        setRoutes(data.data);
      }
    } catch (error) {
      console.error('Error fetching routes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRouteSubmit = async (routeData) => {
      // POST to /api/routes
      if (!firebaseUser) return;
      
      try {
        const token = await firebaseUser.getIdToken();
        console.log('Submitting route to:', `${API_BASE_URL}/api/routes`);

        const res = await fetch(`${API_BASE_URL}/api/routes`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(routeData)
        });

        if (!res.ok) {
          const errText = await res.text(); // Read text body if not OK (e.g. 404 HTML)
          console.error('Route submission failed:', res.status, res.statusText, errText);
          alert(`Failed to save route. Server returned: ${res.status} ${res.statusText}`);
          return;
        }

        const data = await res.json();
        if (data.success) {
            // Re-fetch
            fetchRoutes();
            setIsPopupOpen(false);
        } else {
            alert(data.message || 'Failed to create route');
        }
      } catch (error) {
        console.error('Error submitting route:', error);
        alert('An error occurred while saving the route. See console for details.');
      }
  };

  const handleEditSave = async (updatedData) => {
    if (!editingRoute || !firebaseUser) return;
    try {
      const token = await firebaseUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/routes/${editingRoute.id}`, {
        method: 'PUT',
        headers: {
           'Content-Type': 'application/json',
           'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatedData)
      });
      
      if (!res.ok) throw new Error('Failed to update route');
      
      const data = await res.json();
      if (data.success) {
        fetchRoutes(); 
        setEditingRoute(null);
      } else {
        alert(data.message || 'Failed to update');
      }
    } catch (err) {
      console.error(err);
      alert('Error updating route');
    }
  };

  const handleDeleteRoute = async (e, routeId) => {
    e.stopPropagation();
    if (!window.confirm(t('common.confirmDelete', 'Are you sure you want to delete this route?'))) return;
    if (!firebaseUser) return;

    try {
      const token = await firebaseUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/routes/${routeId}`, {
        method: 'DELETE',
        headers: {
           'Authorization': `Bearer ${token}`
        }
      });
      
      if (!res.ok) throw new Error('Failed to delete route');
      
      fetchRoutes(); 
    } catch (err) {
      console.error(err);
      alert('Error deleting route');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilter(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page
  };

  const handleResetFilters = () => {
    setFilter({
      country: '',
      mountainRange: '',
      lengthCategory: '',
      elevationCategory: '',
      difficulty: '',
      routeType: '',
    });
    setSortOrder('elevation-desc');
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Helper to get mountain range / country for a route
  // A route typically belongs to mountains. We check the first mountain to infer country/range
  const getRouteMetadata = (route) => {
    if (!route.mountainIds || route.mountainIds.length === 0) return { country: null, mountainRange: null };
    // Find the mountain in the props
    const mountain = mountains.find(m => String(m.id) === String(route.mountainIds[0]));
    if (!mountain) return { country: null, mountainRange: null };
    return { country: mountain.country, mountainRange: mountain.mountainRange };
  };

  const filteredRoutes = useMemo(() => {
    return routes.filter(route => {
      const { country, mountainRange } = getRouteMetadata(route);

      // Country Filter
      if (filter.country && country !== filter.country) return false;

      // Range Filter
      if (filter.mountainRange && mountainRange !== filter.mountainRange) return false;

      // Length Category
      if (filter.lengthCategory) {
        const kms = route.kms || 0;
        const limits = {
          short: { min: 0, max: 5 },
          medium: { min: 5, max: 10 },
          long: { min: 10, max: 20 },
          epic: { min: 20, max: 9999 }
        }[filter.lengthCategory];
        if (limits && (kms < limits.min || kms >= limits.max)) return false;
      }

      // Elevation Category
      if (filter.elevationCategory) {
        const elev = route.elevationGain || 0;
        const limits = {
          flattish: { min: 0, max: 200 },
          mild: { min: 200, max: 500 },
          challenging: { min: 500, max: 1000 },
          extreme: { min: 1000, max: 9999 }
        }[filter.elevationCategory];
        if (limits && (elev < limits.min || elev >= limits.max)) return false;
      }

      // Difficulty
      if (filter.difficulty && route.difficulty !== filter.difficulty) return false;

      // Route Type
      if (filter.routeType && route.routeType !== filter.routeType) return false;

      return true;
    }).sort((a, b) => {
      // Sort logic
      // elevation-asc, elevation-desc, length-asc, length-desc
      const elevA = a.elevationGain || 0;
      const elevB = b.elevationGain || 0;
      const lenA = a.kms || 0;
      const lenB = b.kms || 0;

      switch (sortOrder) {
        case 'elevation-asc': return elevA - elevB;
        case 'elevation-desc': return elevB - elevA;
        case 'length-asc': return lenA - lenB;
        case 'length-desc': return lenB - lenA;
        default: return 0;
      }
    });
  }, [routes, mountains, filter, sortOrder]);


  const getFilteredTitle = () => {
    const totalCount = routes.length;
    const filteredCount = filteredRoutes.length;

    if (filteredCount === totalCount) {
      return t('routesPage.allTitle', 'All Routes ({{count}})', { count: totalCount });
    }

    const activeFilters = [];
    if (filter.country) activeFilters.push(filter.country);
    if (filter.mountainRange) activeFilters.push(filter.mountainRange);
    if (filter.lengthCategory) activeFilters.push(t(`routesPage.filters.length.${filter.lengthCategory}`, filter.lengthCategory));
    if (filter.elevationCategory) activeFilters.push(t(`routesPage.filters.elevation.${filter.elevationCategory}`, filter.elevationCategory));
    if (filter.difficulty) activeFilters.push(t(`routesPage.filters.difficulty.${filter.difficulty}`, filter.difficulty));
    if (filter.routeType) activeFilters.push(t(`routesPage.filters.routeType.${filter.routeType}`, filter.routeType));

    if (activeFilters.length === 0) {
      return t('routesPage.filteredCountTitle', 'Filtered Routes ({{filtered}}/{{total}})', { filtered: filteredCount, total: totalCount });
    }

    return t('routesPage.filteredTitle', 'Routes: {{filters}} ({{filtered}}/{{total}})', {
      filters: activeFilters.join(', '),
      filtered: filteredCount,
      total: totalCount
    });
  };

  const totalPages = Math.ceil(filteredRoutes.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRoutes = filteredRoutes.slice(indexOfFirstItem, indexOfLastItem);

  if (loading) {
    return <RoutesPageSkeleton />;
  }

  return (
    <div className={styles.container}>
      <Helmet>
        <title>{t('nav.routes', 'Routes')} | {t('common.siteName', 'Mountains')}</title>
      </Helmet>
      
      <div className={styles.header}>
        <h1 className={styles.title}>
            <svg style={{width:'2rem', height:'2rem'}} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.875 1.875 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.159.69.159 1.006 0Z" />
            </svg>
            {getFilteredTitle()}
        </h1>
        <RouteFilter 
          filter={filter}  
          onFilterChange={handleFilterChange} 
          onResetFilters={handleResetFilters} 
          mountains={mountains} 
          sortOrder={sortOrder}
          onSortChange={setSortOrder}
        />
      </div>

      <div className={styles.grid}>
        {currentRoutes.map(route => {
           const meta = getRouteMetadata(route);
           const matchedMountains = mountains.filter(m => 
              route.mountainIds && (route.mountainIds.includes(m.id) || route.mountainIds.includes(String(m.id)))
           );

           return (
            <div key={route.id} className={styles.card} onClick={() => navigate(`/routes/${route.id}`)}>
               <div className={styles.cardHeader}>
                 <h3 className={styles.cardTitle}>{route.name}</h3>
                 {(currentUser?.admin || currentUser?.editor) && (
                   <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.25rem' }}>
                     <button
                       title={t('action.edit', 'Edit')}
                       style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#666' }}
                       onClick={(e) => { e.stopPropagation(); setEditingRoute(route); }}
                     >
                       <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '1.1rem', height: '1.1rem' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                       </svg>
                     </button>
                     <button 
                       title={t('action.delete', 'Delete')}
                       style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#e53935' }}
                       onClick={(e) => handleDeleteRoute(e, route.id)}
                     >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '1.1rem', height: '1.1rem' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                        </svg>
                     </button>
                   </div>
                 )}
               </div>
               <div className={styles.cardBody}>
                 <div className={styles.statRow}>
                    <span className={styles.statLabel}>{t('common.distance', 'Distance')}:</span>
                    <span className={styles.statValue}>{route.kms ? `${route.kms} km` : '-'}</span>
                 </div>
                 <div className={styles.statRow}>
                    <span className={styles.statLabel}>{t('common.elevationGain', 'Elevation')}:</span>
                    <span className={styles.statValue}>{route.elevationGain ? `${route.elevationGain}m` : '-'}</span>
                 </div>

                 {matchedMountains.length > 0 && (
                    <div className={styles.mountainList}>
                        {matchedMountains.map(m => (
                            <Link 
                                key={m.id} 
                                to={`/mountains/${m.id}`} 
                                className={styles.mountainLink}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {m.name}
                            </Link>
                        ))}
                    </div>
                 )}

                 <div>
                    {route.tags && route.tags.slice(0, 3).map(tag => (
                       <span key={tag} className={styles.badge} style={getTagStyle(tag)}>
                          {t(`mountainPage.routes.tags.${tag}`, tag)}
                       </span>
                    ))}
                 </div>
               </div>
            </div>
          );
        })}

        <div 
          className={`${styles.card} ${styles.addRouteCard}`} 
          onClick={() => currentUser ? setIsPopupOpen(true) : setIsLoginPopupOpen(true)}
        >
             <div className={styles.addRouteContent}>
                <span className={styles.plusIcon}>+</span>
                <span className={styles.addRouteText}>{t('routesPage.addTitle', 'Add New Route')}</span>
             </div>
        </div>
      </div>
      
      <Pagination 
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />

      {isPopupOpen && (
        <RoutePopup 
          onClose={() => setIsPopupOpen(false)} 
          onSubmit={handleRouteSubmit}
          mountains={mountains}
        />
      )}

      {editingRoute && (
        <EditRoutePopup 
          route={editingRoute}
          mountains={mountains}
          onClose={() => setEditingRoute(null)} 
          onSave={handleEditSave}
        />
      )}
      
      {isLoginPopupOpen && <LoginPopup onClose={() => setIsLoginPopupOpen(false)} />}
    </div>
  );
}

export default RoutesPage;
